import { useCallback, useEffect, useRef, useState } from "react";

interface ScrollSpySection {
  id: string;
}

interface ScrollSpyResult {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  activeSection: string;
  scrollToSection: (sectionId: string) => void;
}

function getScrollParent(el: HTMLElement): HTMLElement {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return document.documentElement;
}

export function useScrollSpy(sections: ScrollSpySection[], watch: unknown): ScrollSpyResult {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState(sections[0]?.id ?? "");

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run scroll handler when watched data loads
  useEffect(() => {
    const marker = scrollRef.current;
    if (!marker) return;
    const container = getScrollParent(marker);

    const handleScroll = (): void => {
      const containerRect = container.getBoundingClientRect();
      const threshold = containerRect.top + 80;

      let current = sections[0]?.id ?? "";
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= threshold) {
          current = section.id;
        }
      }
      setActiveSection(current);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => container.removeEventListener("scroll", handleScroll);
  }, [watch]);

  const scrollToSection = useCallback((sectionId: string) => {
    const marker = scrollRef.current;
    const el = document.getElementById(sectionId);
    if (!marker || !el) return;
    const container = getScrollParent(marker);

    // Land the section just below the sticky page header inside the scroller
    const headerOffset = 76;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const target = elRect.top - containerRect.top + container.scrollTop - headerOffset;
    const start = container.scrollTop;
    const distance = target - start;
    if (distance === 0) return;

    // Fixed short duration so long jumps feel snappy, unlike native smooth scroll
    const duration = 220;
    const startTime = performance.now();
    const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

    const step = (now: number): void => {
      const progress = Math.min((now - startTime) / duration, 1);
      container.scrollTop = start + distance * easeOutCubic(progress);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  return { scrollRef, activeSection, scrollToSection };
}
